<template>
  <div class="flex justify-center px-4 sm:py-12 sm:px-6 lg:px-8">
    <div class="space-y-8 w-full max-w-md">
      <div>
        <img class="mx-auto w-auto h-12" src="/logo.svg" alt="BoxBuddy" />
        <h2 class="text-3xl font-extrabold text-center text-gray-900">
          Verify Your Email
        </h2>
        <p class="mt-2 text-sm text-center text-gray-600">
          Please verify your email address to access all features
        </p>
      </div>

      <div class="overflow-hidden p-6 bg-white shadow sm:rounded-lg">
        <div class="mb-4 text-sm text-gray-600">
          <p>Thanks for signing up! Before getting started, could you verify your email address by clicking on the link we just emailed to you?</p>
          <p class="mt-2">If you didn't receive the email, we will gladly send you another.</p>
        </div>

        <div v-if="verificationLinkSent" class="p-4 mb-4 bg-green-50 rounded-md">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="w-5 h-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm font-medium text-green-800">
                A new verification link has been sent to your email address.
              </p>
            </div>
          </div>
        </div>

        <div v-if="error" class="p-4 mb-4 bg-red-50 rounded-md">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="w-5 h-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm font-medium text-red-800">
                {{ error }}
              </p>
            </div>
          </div>
        </div>

        <div v-if="resendCounter" class="text-sm text-gray-600">
          <p class="flex items-center">
            <svg class="mr-1.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Didn't receive it? Check spam or wait <span class="inline-block ml-1 font-semibold text-green-800">{{ resendCounter }}s</span>
          </p>
        </div>

        <div class="items-center mt-6 space-y-2 sm:space-y-0 sm:flex sm:justify-between">
          <button
            v-if="!resendCounter"
            @click="resendVerificationEmail"
            :disabled="isSubmitting"
            class="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md border border-transparent shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            <svg v-if="isSubmitting" class="mr-2 -ml-1 w-4 h-4 text-white animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Resend Verification Email
          </button>


          <div>
            <RouterLink
              :to="{ name: ROUTES.PROFILE }"
              class="text-sm font-medium text-indigo-600 hover:text-indigo-500"
            >
              Edit Profile
            </RouterLink>

            <button
              @click="logout"
              class="ml-4 text-sm font-medium text-indigo-600 hover:text-indigo-500"
            >
              Log Out
            </button>
          </div>
        </div>
      </div>

      <div class="mt-6">
        <div class="relative">
          <div class="flex absolute inset-0 items-center">
            <div class="w-full border-t border-gray-300"></div>
          </div>
          <div class="flex relative justify-center text-sm">
            <span class="px-2 text-gray-500 bg-gray-50">
              Need help?
            </span>
          </div>
        </div>
        <div class="mt-6 text-center">
          <a href="mailto:support@boxbuddy.me" class="font-medium text-indigo-600 hover:text-indigo-500">
            Contact Support
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { ROUTES } from '@/router/routeNames';
import { useAuthStore } from '@/stores/authStore';
import { useToast } from 'primevue/usetoast';
import api from '@/services/axios';
import mixpanel from '@/services/mixpanel';

const router = useRouter();
const authStore = useAuthStore();
const toast = useToast();

const verificationLinkSent = ref(false);
const isSubmitting = ref(false);
const error = ref(null);
const user = authStore.user;
const resendCounter = ref(0);
const resendInterval = ref(null);
// Variables to track polling for verification status
const pollingInterval = ref(null);
const checkingStatus = ref(false);
const startCounter = () => {
  resendCounter.value = 30; // Set to 30 when starting
  resendInterval.value = setInterval(() => {
    resendCounter.value--;
    if (resendCounter.value <= 0) {
      endCounter();
    }
  }, 1000);
}

const endCounter = () => {
  if (resendInterval.value) {
    clearInterval(resendInterval.value); // Correct syntax
    resendInterval.value = null;
  }
}

// watch(resendCounter, (val) => {
//   if(!val) endCounter()
// })
// Check if user is verified and set up polling
onMounted(() => {
  // If user is already verified, redirect to dashboard
  if (user && user.email_verified_at) {
    router.push({ name: ROUTES.DASHBOARD });
    return;
  }

  // If user is not logged in, redirect to login
  if (!user) {
    router.push({ name: ROUTES.LOGIN });
    return;
  }

  // Start polling for verification status
  startPollingForVerification();
});

// Clean up polling interval when component is unmounted
onUnmounted(() => {
  endCounter();
  stopPollingForVerification();
});

// Start polling for verification status
const startPollingForVerification = () => {
  // Poll every 5 seconds to check if email has been verified
  pollingInterval.value = setInterval(checkVerificationStatus, 5000);
};

// Stop polling for verification status
const stopPollingForVerification = () => {
  if (pollingInterval.value) {
    clearInterval(pollingInterval.value);
    pollingInterval.value = null;
  }
};

// Check if the user's email has been verified
const checkVerificationStatus = async () => {
  if (checkingStatus.value) return; // Prevent multiple simultaneous checks

  try {
    checkingStatus.value = true;

    // Fetch fresh user data to check verification status
    await authStore.getUser(true);

    // If email is now verified, redirect to dashboard
    if (authStore.user && authStore.user.email_verified_at) {
      stopPollingForVerification();

      toast.add({
        severity: 'success',
        summary: 'Email Verified',
        detail: 'Your email has been verified successfully!',
        life: 5000
      });

      // Short delay before redirecting
      setTimeout(() => {
        router.push({ name: ROUTES.DASHBOARD });
      }, 1500);
    }
  } catch (err) {
    console.error('Failed to check verification status:', err);
  } finally {
    checkingStatus.value = false;
  }
};

// Resend verification email
const resendVerificationEmail = async () => {
  try {
    isSubmitting.value = true;
    error.value = null;

    await api.post('api/email/verification-notification');

    verificationLinkSent.value = true;
    mixpanel.track('Resend Verification Code Button Pressed', {
      distinct_id: user.id,
      name: user.name,
      email: user.email,
      phone: user.phone,
    });
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Verification link sent! Please check your email.',
      life: 5000
    });
    startCounter();
  } catch (err) {
    console.error('Failed to send verification email:', err);

    error.value = 'Failed to send verification email. Please try again later.';

    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to send verification email. Please try again later.',
      life: 5000
    });
  } finally {
    isSubmitting.value = false;
  }
};

// Logout function
const logout = async () => {
  try {
    await authStore.logout();
    router.push({ name: ROUTES.LOGIN });
  } catch (err) {
    console.error('Failed to logout:', err);

    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to logout. Please try again.',
      life: 3000
    });
  }
};
</script>
